import { useEffect, useLayoutEffect, useState, useSyncExternalStore, type RefObject } from 'react';

/**
 * Layout of the Subject Space Tutor panel (study-subject-tutor-panel-prd.md §3.2):
 * - `dock`: ≥1280 px wide and taller than 500 px: a 400 px right dock that collapses to 56 px;
 * - `overlay`: 1024–1279 px: a 400 px drawer over the page with a backdrop;
 * - `sheet`: below 1024 px, or a visible height of 500 px or less: a full-screen sheet.
 */
export type TutorPanelLayout = 'dock' | 'overlay' | 'sheet';

/**
 * Study Home: the Tutor panel can switch subject (study-my-year-prd.md §4.6, owner 2026-10-09).
 * Subject Space pages pass none: there the subject comes from the page.
 */
export interface TutorSubjectSwitch {
  choices: ReadonlyArray<{ key: string; name: string }>;
  onChange: (subjectKey: string) => void;
}

export const DOCK_QUERY = '(min-width: 1280px) and (min-height: 501px)';
export const OVERLAY_QUERY = '(min-width: 1024px) and (min-height: 501px)';

function readLayout(): TutorPanelLayout {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dock';
  if (window.matchMedia(DOCK_QUERY).matches) return 'dock';
  if (window.matchMedia(OVERLAY_QUERY).matches) return 'overlay';
  return 'sheet';
}

export function useTutorPanelLayout(): TutorPanelLayout {
  const [layout, setLayout] = useState(readLayout);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const queries = [window.matchMedia(DOCK_QUERY), window.matchMedia(OVERLAY_QUERY)];
    const update = () => setLayout(readLayout());
    queries.forEach((query) => query.addEventListener?.('change', update));
    window.addEventListener('resize', update);
    return () => {
      queries.forEach((query) => query.removeEventListener?.('change', update));
      window.removeEventListener('resize', update);
    };
  }, []);
  return layout;
}

/**
 * The Tutor composers (Ask and the mind map `Change my map`). Below `sm` the message box takes a
 * full-width row and the button sits full width under it, so neither the box nor its
 * placeholder is squeezed; from `sm` they sit side by side.
 */
export const TUTOR_COMPOSER_FORM = 'flex flex-col gap-2 sm:flex-row sm:items-end';
export const TUTOR_COMPOSER_BOX = 'w-full sm:w-auto sm:min-w-0 sm:flex-1';
export const TUTOR_COMPOSER_SUBMIT =
  'flex w-full shrink-0 flex-col items-stretch sm:w-auto sm:items-center';

/** Collapse state is remembered per learner on this device (§3.5); default open. */
const COLLAPSE_KEY_PREFIX = 'airbotix.study.tutor-panel.v1:';

export function readDockCollapsed(kidId: string | null): boolean {
  try {
    return (
      window.localStorage.getItem(`${COLLAPSE_KEY_PREFIX}${kidId ?? 'unknown'}`) === 'collapsed'
    );
  } catch {
    return false;
  }
}

export function writeDockCollapsed(kidId: string | null, collapsed: boolean) {
  try {
    window.localStorage.setItem(
      `${COLLAPSE_KEY_PREFIX}${kidId ?? 'unknown'}`,
      collapsed ? 'collapsed' : 'open',
    );
  } catch {
    // Storage can be blocked; the panel still works and opens by default next time.
  }
}

/**
 * The Tutor panel inside the mind map editor (§4.8, D-TPN-12) starts closed. Open or closed is
 * remembered per learner on this device; blocked storage keeps the default.
 */
const MIND_MAP_TUTOR_KEY_PREFIX = 'airbotix.study.mind-map-tutor.v1:';

export function readMindMapTutorOpen(kidId: string | null): boolean {
  try {
    return (
      window.localStorage.getItem(`${MIND_MAP_TUTOR_KEY_PREFIX}${kidId ?? 'unknown'}`) === 'open'
    );
  } catch {
    return false;
  }
}

export function writeMindMapTutorOpen(kidId: string | null, open: boolean) {
  try {
    window.localStorage.setItem(
      `${MIND_MAP_TUTOR_KEY_PREFIX}${kidId ?? 'unknown'}`,
      open ? 'open' : 'closed',
    );
  } catch {
    // Storage can be blocked; the panel still works and starts closed next time.
  }
}

/** The dock starts below the Study app bar and follows its height (same method as tool Tutor). */
export function useDockTop(ref: RefObject<HTMLElement>, enabled: boolean): number {
  const [top, setTop] = useState(0);
  useLayoutEffect(() => {
    if (!enabled) return;
    const shell = ref.current?.closest<HTMLElement>('.study-shell');
    const header = shell?.querySelector<HTMLElement>(':scope > header');
    if (!header) return;
    const measure = () => setTop(header.offsetTop + header.offsetHeight);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(header);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref, enabled]);
  return top;
}

/** True while a phone software keyboard takes the bottom of the screen. */
export function keyboardOpen(viewportHeight: number): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerHeight - viewportHeight > 150;
}

/**
 * Open material previews (a full-screen `z-50` modal). Below 1280 px the `Ask Tuan Tuan` launcher
 * is `z-40` and would sit under the preview backdrop, so the learner could not ask about the
 * material it shows (§3.2, §4.3). While a preview is open the launcher is raised above it.
 */
let openMaterialPreviews = 0;
const materialPreviewListeners = new Set<() => void>();

function notifyMaterialPreview() {
  materialPreviewListeners.forEach((listener) => listener());
}

/** Call from a material preview: the launcher stays above it while it is mounted. */
export function useMaterialPreviewOpenSignal() {
  useEffect(() => {
    openMaterialPreviews += 1;
    notifyMaterialPreview();
    return () => {
      openMaterialPreviews -= 1;
      notifyMaterialPreview();
    };
  }, []);
}

/** True while a material preview is open on this screen. */
export function useMaterialPreviewOpen(): boolean {
  return useSyncExternalStore(
    (listener) => {
      materialPreviewListeners.add(listener);
      return () => materialPreviewListeners.delete(listener);
    },
    () => openMaterialPreviews > 0,
    () => false,
  );
}

/**
 * Full-screen Study dialogs keep their own UI: the mind map example viewer (D-TPN-11) and the
 * preview of a new AI map are full-screen dialogs over the Subject page, so they signal while
 * they are open and the Subject Tutor panel and its phone launcher hide. The mind map editor is
 * not one of them any more: it shows Tuan Tuan inside its own layout (D-TPN-12).
 */
let openFullScreenPages = 0;
const fullScreenListeners = new Set<() => void>();

function notifyFullScreen() {
  fullScreenListeners.forEach((listener) => listener());
}

/** Call from a full-screen Study page or dialog: the Tutor panel hides while it is mounted. */
export function useFullScreenStudyPageSignal() {
  useEffect(() => {
    openFullScreenPages += 1;
    notifyFullScreen();
    return () => {
      openFullScreenPages -= 1;
      notifyFullScreen();
    };
  }, []);
}

/** True while a full-screen Study page or dialog is open on this screen. */
export function useFullScreenStudyPageOpen(): boolean {
  return useSyncExternalStore(
    (listener) => {
      fullScreenListeners.add(listener);
      return () => fullScreenListeners.delete(listener);
    },
    () => openFullScreenPages > 0,
    () => false,
  );
}

/** The mind map editor route `/learn/study/subjects/:subjectKey/mind-maps/:mapId`. */
const MIND_MAP_EDITOR_PATH = /^\/learn\/study\/subjects\/[^/]+\/mind-maps\/[^/]+\/?$/;

/**
 * True on the mind map editor route. The editor renders its own Tutor panel beside the canvas
 * (D-TPN-12), so a Subject Space panel slot there stays empty: one Tutor per screen.
 */
export function isMindMapEditorPath(pathname: string): boolean {
  return MIND_MAP_EDITOR_PATH.test(pathname);
}
